import { useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import AppContext from '../../../contexts/AppContext';
import db from '../../../db/db';
import {
  autoSyncDownloads,
  cancelAutoSyncRetry,
  heldSignaturesNote,
  refusedSignaturesNote,
  reviewsRaisedNote,
  subscribeSyncStatus,
} from '../../../utils/offlineSyncCoordinator';
import {
  clearExpiredDownloads,
  isFirstInspectionLine,
  reviewKeepsDownload,
} from '../../../utils/offlineDownloadRemoval';
import { offerDownloadRemoval } from './RemoveDownloadPrompt';

/** One toast per project, so one project's result never replaces another's. */
const toastIdFor = (projectId: number) => `offline-auto-sync-${projectId}`;

/** Update the project's toast if it is still on screen, otherwise show it again. */
const show = (toastId: string, render: string, options: any) => {
  if (toast.isActive(toastId)) {
    toast.update(toastId, { render, isLoading: false, closeButton: true, ...options });
  } else {
    toast(render, { toastId, ...options });
  }
};

/**
 * Signatures the server refused, with its reason: the sync has taken them off the device, so the
 * user must sign again. Its own toast, kept until closed; one per project, so a manual sync that
 * joins an automatic run doesn't show it twice.
 */
export const reportRefusedSignatures = (projectId: number, refused: any[] = []) => {
  if (!refused.length) return;

  toast.error(refusedSignaturesNote(refused), { toastId: `offline-refused-${projectId}`, autoClose: false });
};

let legacyReported = false;

/**
 * SW2-2459 — uploads offline work on its own: when the app starts, when the device comes back
 * online and when the user returns to the app. Renders nothing; reports each automatic run as a
 * toast for its project.
 */
const OfflineAutoSync = () => {
  const appContext = useContext(AppContext);
  const userId: number | undefined = appContext.get('user.id');
  // SW2-2474: a session restored offline is not uploaded under until the server
  // has confirmed it (OfflineSessionGate) — the user may have been deactivated.
  const unverified = Boolean(appContext.get('sessionUnverified'));

  useEffect(() => {
    if (!userId || unverified) return undefined;

    const onLegacy = (projects: any[]) => {
      if (legacyReported) return;

      legacyReported = true;
      toast.info(
        `${projects.map((p) => p.name).join(', ')}: offline work from an earlier download needs one manual sync. Open the dashboard and press Sync.`,
        { autoClose: false },
      );
    };
    const upload = () => autoSyncDownloads({ userId, onLegacy }).catch(() => {});
    // SW2-2459: downloads whose 24 hours are up (and that have nothing waiting) go — at start and on
    // reconnect only. Returning to the app is too often: on a phone, taking a photo does it, and
    // the download the inspector is working in went mid-inspection (PR #156 review).
    const sync = () => {
      upload().then(() => clearExpiredDownloads(userId));
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') upload();
    };

    const unsubscribe = subscribeSyncStatus(async (status: any) => {
      if (!status.background) return;

      const toastId = toastIdFor(status.projectId);
      const project = await db.projects.get(status.projectId).catch(() => undefined);
      const name = project?.name || 'a downloaded project';

      if (status.syncing) {
        show(toastId, `Uploading offline work for ${name}… keep the app open.`, {
          type: toast.TYPE.DEFAULT,
          isLoading: true,
          autoClose: false,
          closeButton: false,
        });

        return;
      }

      // Missing, or stopped because another user signed in: nothing for this user to act on.
      if (status.missing || status.sessionChanged) {
        toast.dismiss(toastId);

        return;
      }

      reportRefusedSignatures(status.projectId, status.refusedSignatures);

      if (status.error) {
        show(
          toastId,
          `Upload for ${name} stopped because of an error. Your work is still saved on this device. Press Sync on the dashboard, or contact support if this keeps happening.`,
          { type: toast.TYPE.ERROR, autoClose: false },
        );

        return;
      }

      if (status.interrupted) {
        show(toastId, `Upload paused for ${name}: no connection. It will continue when you're back online.`, {
          type: toast.TYPE.INFO,
          autoClose: 6000,
        });

        return;
      }

      if (status.remaining > 0) {
        const items = `${status.remaining} item${status.remaining === 1 ? '' : 's'}`;

        show(
          toastId,
          `Sync incomplete for ${name}: ${items} could not be uploaded. Your work is still saved on this device.${heldSignaturesNote(status.heldSignatures)}`,
          { type: toast.TYPE.ERROR, autoClose: false },
        );

        return;
      }

      const reviews = reviewsRaisedNote(status.sentSignatures);

      show(toastId, `Offline work for ${name} uploaded.${reviews}`, {
        type: toast.TYPE.SUCCESS,
        // Long enough to read the review sentence.
        autoClose: reviews ? 8000 : 4000,
      });

      // SW2-2459 × SW2-2248: a signature that raised a review leaves the inspection open: the
      // reviewer can send it back to be edited, so the download is kept (and no 24-hour clear is
      // scheduled — offerDownloadRemoval is what schedules it). The 3-day cleanup still applies.
      if (await reviewKeepsDownload(status.sentSignatures)) return;

      // SW2-2459: the sync just sent the user's own first inspection signature and nothing is
      // left — offer to remove the download.
      for (const sig of status.sentSignatures || []) {
        if (sig.group === 'inspection' && (await isFirstInspectionLine(sig.inspectionId, sig.lineType))) {
          offerDownloadRemoval(status.projectId).catch((err: any) =>
            // eslint-disable-next-line no-console
            console.error(`Offering to remove download ${status.projectId} failed`, err),
          );
          break;
        }
      }
    });

    sync();
    window.addEventListener('online', sync);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      unsubscribe();
      window.removeEventListener('online', sync);
      document.removeEventListener('visibilitychange', onVisible);
      cancelAutoSyncRetry();
    };
  }, [userId, unverified]);

  return null;
};

export default OfflineAutoSync;
